/**
 * Listas da marca — as decisões PURAS (sem Office.js), verificadas fora do Word
 * por `npm run check:lists`. A execução no documento mora em `word/lists.ts`.
 *
 * A lista da marca é uma lista NATIVA do Word: o marcador ("/" ou "1.") é do
 * NÍVEL da lista, não um caractere digitado no texto. Até set/2026 o bullet era
 * um glifo digitado, e dele vinham três defeitos vistos pelo QA: o item perdia
 * o estilo ao sair da numeração do template (virava "Normal" alinhado à
 * esquerda), não havia subitem, e trocar bullets por numeração era impossível.
 * O que sobra de marcador DIGITADO (do modelo, do usuário, de documento antigo)
 * é reconhecido aqui para sair do texto quando o parágrafo vira item de lista.
 */
/**
 * Quantos níveis a lista da marca define. A norma pede no máximo DOIS
 * (`list_contract.max_levels`); o terceiro existe só para preservar a
 * hierarquia de um documento que já chegou com ele, em vez de achatá-la.
 */
export const LIST_LEVEL_COUNT = 3;
/** Nível pedido pelo modelo (1 = item, 2 = subitem) → índice do Word (0-based). */
export function levelIndexFromInput(level) {
    const n = Math.round(Number(level));
    if (!Number.isFinite(n) || n < 1)
        return 0;
    return Math.min(n, LIST_LEVEL_COUNT) - 1;
}
/**
 * Níveis válidos para uma sequência de itens, como num sumário: o nível de um
 * item é quantos ANCESTRAIS ele tem de fato — itens com nível menor antes dele,
 * sem um de nível igual ou menor no meio. Um nível pulado não cria pai que não
 * existe ([item, nível 3, nível 3] vira item + dois subitens irmãos), e a
 * lista nova não começa em subitem. Sem isso o Word desenha um marcador de
 * nível cujo pai não existe.
 *
 * `prevLevel`: nível do item logo acima quando os itens CONTINUAM uma lista
 * (o 1º pode ser subitem dele); −1 numa lista nova.
 */
export function normalizeLevels(levels, prevLevel = -1) {
    // Níveis pedidos dos ancestrais do próximo item; a continuação herda a
    // cadeia do item de cima (níveis 0..prevLevel).
    const cadeia = [];
    for (let n = 0; n <= Math.min(prevLevel, LIST_LEVEL_COUNT - 1); n++)
        cadeia.push(n);
    return levels.map((bruto) => {
        const pedido = Math.max(0, Math.round(Number(bruto) || 0));
        while (cadeia.length && cadeia[cadeia.length - 1] >= pedido)
            cadeia.pop();
        const nivel = Math.min(cadeia.length, LIST_LEVEL_COUNT - 1);
        cadeia.push(pedido);
        return nivel;
    });
}
/**
 * Itens de `insert_list`: texto puro (nível 1) ou `{ text, level }`. Tolerante
 * ao que o modelo manda de fato: nível como texto ("2"), item vazio (sai) e
 * quebra de linha dentro do item (vira espaço — um item é UM parágrafo).
 */
export function parseListItems(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        let text;
        let level = 0;
        if (typeof item === 'string') {
            text = item;
        }
        else if (item && typeof item === 'object' && typeof item.text === 'string') {
            text = item.text;
            level = levelIndexFromInput(item.level);
        }
        else {
            continue;
        }
        const limpo = text.replace(/\r?\n/g, ' ');
        if (!limpo.trim())
            continue;
        out.push({ text: limpo, level });
    }
    return out;
}
// Marcador digitado: o sinal + espaço/tab + conteúdo. Sem o espaço não é
// marcador — "/agent/step", "-15%" e "1.5 ponto" são texto.
const TYPED_BULLET = /^[ \t]*[/\-–—•·*◦‣▪►][ \t]+(?=\S)/;
// "1." "1)" "1.2." "a)" "b." "A)" "iv)". Letra maiúscula só com ")": "J. Silva"
// abrindo um parágrafo é nome, não marcador.
const TYPED_NUMBER = /^[ \t]*(?:\d{1,3}(?:\.\d{1,3})*[.)]|[a-z][.)]|[A-Z]\)|(?:i{1,3}|iv|vi{0,3}|ix|x)[.)])[ \t]+(?=\S)/;
/** Marcador de lista DIGITADO no começo do texto, se houver. */
export function typedListMarker(text) {
    const b = TYPED_BULLET.exec(text || '');
    if (b)
        return { marker: b[0], kind: 'bullet' };
    const n = TYPED_NUMBER.exec(text || '');
    if (n)
        return { marker: n[0], kind: 'numbered' };
    return null;
}
/** Texto sem o marcador digitado do começo (o marcador passa a ser o da lista). */
export function stripTypedListMarker(text) {
    const m = typedListMarker(text);
    return m ? text.slice(m.marker.length) : text;
}
/**
 * Tipo da lista a partir de `List.levelTypes` do Office.js. "Picture" é bullet
 * de imagem — continua sendo bullet para decidir se uma lista vizinha é do
 * mesmo tipo.
 */
export function listKindOfLevelType(levelType) {
    const t = String(levelType !== null && levelType !== void 0 ? levelType : '').toLowerCase();
    if (t === 'number')
        return 'numbered';
    if (t === 'bullet' || t === 'picture')
        return 'bullet';
    return null;
}
/**
 * Texto de busca do Word para achar o marcador: tab é `^t` e o circunflexo, o
 * caractere de escape, dobra. Medido no Word: a busca por "/^t" acha o "/\t"
 * do começo do item, e apagar só esse trecho preserva a formatação do resto.
 */
export function wordSearchText(marker) {
    return marker.replace(/\^/g, '^^').replace(/\t/g, '^t');
}
/**
 * Agrupa entradas de lista em LISTAS: índices consecutivos do mesmo tipo são
 * uma lista só. Um parágrafo fora do pedido no meio, ou a troca de tipo,
 * começa outra — é o que o Word faria com o mesmo documento.
 */
export function groupListRuns(entries) {
    const ordenadas = [...entries].sort((a, b) => a.index - b.index);
    const grupos = [];
    for (const e of ordenadas) {
        const atual = grupos[grupos.length - 1];
        const ultimo = atual === null || atual === void 0 ? void 0 : atual[atual.length - 1];
        if (ultimo && ultimo.index + 1 === e.index && ultimo.kind === e.kind)
            atual.push(e);
        else
            grupos.push([e]);
    }
    return grupos;
}
/**
 * Pontuação da norma para itens de lista (`list_contract` do _base/rules.json:
 * `intermediate_items_end_with_semicolon` e `last_item_ends_with_period`) —
 * `npm run check:lists` confere que os dois dizem a mesma coisa.
 */
export const LIST_PUNCTUATION = { intermediate: ';', last: '.' };
// Já termina em pontuação (mesmo antes de aspas/parêntese de fechamento).
const TERMINA_PONTUADO = /[.;:!?…]["'”’)\]]*\s*$/;
/**
 * Acrescenta a pontuação que FALTA nos itens de uma lista NOVA: ';' nos
 * intermediários e '.' no último. Só acrescenta — item que já termina em
 * pontuação fica como está, nada é trocado — e não mexe em item que abre
 * subitens (o pai de um subitem pode terminar em ':' ou em nada; a norma não
 * diz). Visto com o modelo real: ele mandou as etapas sem ';' e respondeu ao
 * usuário que tinha seguido a convenção.
 */
export function punctuateListItems(items) {
    let added = 0;
    const out = items.map((it, i) => {
        const proximo = items[i + 1];
        if (proximo && proximo.level > it.level)
            return it; // abre subitens
        const limpo = it.text.replace(/\s+$/, '');
        if (!limpo || TERMINA_PONTUADO.test(limpo))
            return it;
        added++;
        return Object.assign(Object.assign({}, it), { text: limpo + (proximo ? LIST_PUNCTUATION.intermediate : LIST_PUNCTUATION.last) });
    });
    return { items: out, added };
}
/**
 * Tipo da lista que `insert_list` vai criar. `ordered` explícito manda. Sem ele,
 * itens que TODOS chegam numerados à mão ("1. Levantar", "2. Validar") pedem
 * lista numerada: virar bullets deixaria "/ 1. Levantar" — o "bullets e
 * números juntos" relatado pelo QA.
 */
export function insertListKind(ordered, texts) {
    if (ordered === true)
        return 'numbered';
    if (ordered === false)
        return 'bullet';
    const numerados = texts.filter((t) => { var _a; return ((_a = typedListMarker(t)) === null || _a === void 0 ? void 0 : _a.kind) === 'numbered'; }).length;
    return texts.length >= 2 && numerados === texts.length ? 'numbered' : 'bullet';
}
